/**
 * Cálculos de nutrición (Fase PRO).
 * Funciones puras y sin dependencias. Este módulo puede ejecutarse con
 * `node src/lib/nutrition-formulas.ts` para verificar sus casos de prueba.
 */

export type Sex = 'male' | 'female';

export type ActivityLevel =
  | 'sedentary'
  | 'light'
  | 'moderate'
  | 'active'
  | 'very_active';

export type CompFormula = 'jp3' | 'jp7' | 'durnin' | 'yuhasz';

export type DensityEquation = 'siri' | 'brozek';

export type BmrFormula = 'mifflin' | 'katch';

export type EnergyGoal = 'deficit' | 'maintenance' | 'surplus';

export interface SiteDef {
  key: string;
  label: string;
}

export const SKINFOLD_SITES: SiteDef[] = [
  { key: 'triceps', label: 'Tríceps' },
  { key: 'subscapular', label: 'Subescapular' },
  { key: 'biceps', label: 'Bíceps' },
  { key: 'iliac', label: 'Cresta ilíaca' },
  { key: 'supraspinal', label: 'Supraespinal' },
  { key: 'abdominal', label: 'Abdominal' },
  { key: 'thigh', label: 'Muslo' },
  { key: 'calf', label: 'Pantorrilla' },
  { key: 'midaxillary', label: 'Axilar medio' },
  { key: 'chest', label: 'Pecho' },
];

export const PERIMETER_SITES: SiteDef[] = [
  { key: 'neck', label: 'Cuello' },
  { key: 'shoulder', label: 'Hombro' },
  { key: 'chest', label: 'Tórax' },
  { key: 'abdomen', label: 'Abdomen' },
  { key: 'relaxed_arm', label: 'Brazo relajado' },
  { key: 'flexed_arm', label: 'Brazo contraído' },
  { key: 'forearm', label: 'Antebrazo' },
  { key: 'wrist', label: 'Muñeca' },
  { key: 'thigh', label: 'Muslo' },
  { key: 'calf', label: 'Pantorrilla' },
];

export const DIAMETER_SITES: SiteDef[] = [
  { key: 'biacromial', label: 'Biacromial' },
  { key: 'biiliac', label: 'Biiliocrestal' },
  { key: 'humerus', label: 'Humeral' },
  { key: 'femur', label: 'Femoral' },
  { key: 'bistyloid', label: 'Biestiloideo' },
];

export const ACTIVITY_FACTORS: Record<ActivityLevel, number> = {
  sedentary: 1.2,
  light: 1.375,
  moderate: 1.55,
  active: 1.725,
  very_active: 1.9,
};

export const ACTIVITY_LABELS: Record<ActivityLevel, string> = {
  sedentary: 'Sedentario (poco o nada de ejercicio)',
  light: 'Ligero (1-3 días/semana)',
  moderate: 'Moderado (3-5 días/semana)',
  active: 'Activo (6-7 días/semana)',
  very_active: 'Muy activo (atleta / trabajo físico)',
};

export const FORMULA_LABELS: Record<CompFormula, string> = {
  jp3: 'Jackson-Pollock 3 pliegues',
  jp7: 'Jackson-Pollock 7 pliegues',
  durnin: 'Durnin-Womersley 4 pliegues',
  yuhasz: 'Yuhasz 6 pliegues',
};

export const GOAL_LABELS: Record<EnergyGoal, string> = {
  deficit: 'Déficit (pérdida de grasa)',
  maintenance: 'Mantenimiento',
  surplus: 'Superávit (ganancia)',
};

export type SiteValues = Record<string, number | string | null | undefined>;

interface SiteSum {
  sum: number;
  missing: string[];
}

const sumSites = (values: SiteValues, keys: string[]): SiteSum => {
  let sum = 0;
  const missing: string[] = [];
  for (const key of keys) {
    const v = values?.[key];
    if (v === null || v === undefined || v === '' || isNaN(Number(v))) {
      missing.push(key);
    } else {
      sum += Number(v);
    }
  }
  return { sum, missing };
};

const siri = (density: number) => 495 / density - 450;
const brozek = (density: number) => 457 / density - 414.2;

const applyDensityEquation = (density: number, equation: DensityEquation) =>
  equation === 'brozek' ? brozek(density) : siri(density);

const ageBand = (age: number): 0 | 1 | 2 | 3 | 4 => {
  if (age < 20) return 0;
  if (age < 30) return 1;
  if (age < 40) return 2;
  if (age < 50) return 3;
  return 4;
};

// Durnin & Womersley: D = b - m * log10(S)
const DURNIN_MEN = [
  [1.162, 0.063],
  [1.1631, 0.0632],
  [1.1422, 0.0544],
  [1.162, 0.07],
  [1.1715, 0.0779],
];
const DURNIN_WOMEN = [
  [1.1549, 0.0678],
  [1.1599, 0.0717],
  [1.1423, 0.0632],
  [1.1333, 0.0612],
  [1.1339, 0.0645],
];

export interface CompositionInput {
  sex: Sex;
  ageYears: number;
  formula: CompFormula;
  densityEquation?: DensityEquation;
  skinfolds: SiteValues;
  weightKg?: number | null;
}

export interface CompositionResult {
  ok: boolean;
  percentFat: number | null;
  bodyDensity: number | null;
  fatMassKg: number | null;
  leanMassKg: number | null;
  missing: string[];
  note?: string;
}

const round = (n: number, decimals = 2) => {
  const f = Math.pow(10, decimals);
  return Math.round(n * f) / f;
};

export const computeComposition = (input: CompositionInput): CompositionResult => {
  const { sex, ageYears, formula, skinfolds, weightKg } = input;
  const equation: DensityEquation = input.densityEquation || 'siri';
  const empty: CompositionResult = {
    ok: false,
    percentFat: null,
    bodyDensity: null,
    fatMassKg: null,
    leanMassKg: null,
    missing: [],
  };

  if (formula === 'yuhasz') {
    const { sum, missing } = sumSites(skinfolds, [
      'triceps',
      'subscapular',
      'supraspinal',
      'abdominal',
      'thigh',
      'calf',
    ]);
    if (missing.length) return { ...empty, missing };
    const percentFat = sex === 'female' ? sum * 0.143 + 4.56 : sum * 0.097 + 3.64;
    const fatMassKg = weightKg ? round((weightKg * percentFat) / 100) : null;
    const leanMassKg = weightKg && fatMassKg != null ? round(weightKg - fatMassKg) : null;
    return {
      ok: true,
      percentFat: round(percentFat),
      bodyDensity: null,
      fatMassKg,
      leanMassKg,
      missing: [],
      note: 'Yuhasz estima el % de grasa directamente (sin densidad).',
    };
  }

  let keys: string[];
  if (formula === 'jp3') {
    keys = sex === 'female' ? ['triceps', 'supraspinal', 'thigh'] : ['chest', 'abdominal', 'thigh'];
  } else if (formula === 'durnin') {
    keys = ['biceps', 'triceps', 'subscapular', 'supraspinal'];
  } else {
    // jp7
    keys = ['chest', 'midaxillary', 'triceps', 'subscapular', 'abdominal', 'supraspinal', 'thigh'];
  }

  const { sum, missing } = sumSites(skinfolds, keys);
  if (missing.length) return { ...empty, missing };

  let density: number;
  if (formula === 'jp3') {
    density =
      sex === 'female'
        ? 1.0994921 - 0.0009929 * sum + 0.0000023 * sum * sum - 0.0001392 * ageYears
        : 1.10938 - 0.0008267 * sum + 0.0000016 * sum * sum - 0.0002574 * ageYears;
  } else if (formula === 'durnin') {
    const table = sex === 'female' ? DURNIN_WOMEN : DURNIN_MEN;
    const [b, m] = table[ageBand(ageYears)];
    density = b - m * Math.log10(sum);
  } else {
    density =
      sex === 'female'
        ? 1.097 - 0.00046971 * sum + 0.00000056 * sum * sum - 0.00012828 * ageYears
        : 1.112 - 0.00043499 * sum + 0.00000055 * sum * sum - 0.00028826 * ageYears;
  }

  const percentFat = applyDensityEquation(density, equation);
  const fatMassKg = weightKg ? round((weightKg * percentFat) / 100) : null;
  const leanMassKg = weightKg && fatMassKg != null ? round(weightKg - fatMassKg) : null;

  return {
    ok: true,
    percentFat: round(percentFat),
    bodyDensity: round(density, 5),
    fatMassKg,
    leanMassKg,
    missing: [],
  };
};

export interface IndicesInput {
  weightKg?: number | null;
  heightCm?: number | null;
  waistCm?: number | null;
  hipCm?: number | null;
}

export interface IndicesResult {
  bmi: number | null;
  icc: number | null;
  ict: number | null;
}

export const computeIndices = (input: IndicesInput): IndicesResult => {
  const { weightKg, heightCm, waistCm, hipCm } = input;
  const bmi =
    weightKg && heightCm && heightCm > 0
      ? round(weightKg / Math.pow(heightCm / 100, 2))
      : null;
  const icc = waistCm && hipCm && hipCm > 0 ? round(waistCm / hipCm) : null;
  const ict = waistCm && heightCm && heightCm > 0 ? round(waistCm / heightCm) : null;
  return { bmi, icc, ict };
};

export interface EnergyInput {
  sex: Sex;
  ageYears: number;
  weightKg?: number | null;
  heightCm?: number | null;
  leanMassKg?: number | null;
  activityLevel: ActivityLevel;
  goal?: EnergyGoal;
  bmrFormula?: BmrFormula;
  proteinPerKg?: number;
  fatPerKg?: number;
}

export interface EnergyResult {
  ok: boolean;
  bmr: number | null;
  tdee: number | null;
  targetCalories: number | null;
  proteinG: number | null;
  carbsG: number | null;
  fatG: number | null;
  bmrFormulaUsed: BmrFormula | null;
  missing: string[];
}

const GOAL_CALORIE_FACTOR: Record<EnergyGoal, number> = {
  deficit: 0.85,
  maintenance: 1,
  surplus: 1.1,
};

const DEFAULT_PROTEIN_PER_KG: Record<EnergyGoal, number> = {
  deficit: 2.0,
  maintenance: 1.8,
  surplus: 1.8,
};

const DEFAULT_FAT_PER_KG: Record<EnergyGoal, number> = {
  deficit: 0.8,
  maintenance: 0.9,
  surplus: 1.0,
};

export const computeEnergy = (input: EnergyInput): EnergyResult => {
  const { sex, ageYears, weightKg, heightCm } = input;
  const activityLevel = input.activityLevel || 'moderate';
  const goal: EnergyGoal = input.goal || 'maintenance';

  const missing: string[] = [];
  if (!weightKg) missing.push('weightKg');
  if (!heightCm) missing.push('heightCm');
  if (!ageYears) missing.push('ageYears');
  if (!sex) missing.push('sex');

  if (missing.length) {
    return {
      ok: false,
      bmr: null,
      tdee: null,
      targetCalories: null,
      proteinG: null,
      carbsG: null,
      fatG: null,
      bmrFormulaUsed: null,
      missing,
    };
  }

  let bmr: number;
  let bmrFormulaUsed: BmrFormula;
  if (input.bmrFormula === 'katch' && input.leanMassKg) {
    bmr = 370 + 21.6 * input.leanMassKg;
    bmrFormulaUsed = 'katch';
  } else {
    bmr =
      10 * weightKg! +
      6.25 * heightCm! -
      5 * ageYears +
      (sex === 'female' ? -161 : 5);
    bmrFormulaUsed = 'mifflin';
  }

  const tdee = bmr * (ACTIVITY_FACTORS[activityLevel] ?? 1.55);
  const targetCalories = tdee * GOAL_CALORIE_FACTOR[goal];

  const proteinPerKg = input.proteinPerKg ?? DEFAULT_PROTEIN_PER_KG[goal];
  const fatPerKg = input.fatPerKg ?? DEFAULT_FAT_PER_KG[goal];
  const proteinG = proteinPerKg * weightKg!;
  const fatG = fatPerKg * weightKg!;
  const remaining = targetCalories - proteinG * 4 - fatG * 9;
  const carbsG = Math.max(0, remaining / 4);

  return {
    ok: true,
    bmr: round(bmr),
    tdee: round(tdee),
    targetCalories: round(targetCalories),
    proteinG: round(proteinG),
    carbsG: round(carbsG),
    fatG: round(fatG),
    bmrFormulaUsed,
    missing: [],
  };
};

// ============================================================
// Métricas corporales adicionales (Fase 1 del plan de la clienta)
// - Edad metabólica
// - Grasa visceral (estimada)
// - Masa muscular (derivada)
// ============================================================

/** Nivel de la edad metabólica respecto a la cronológica. */
export type MetabolicAgeLevel = 'younger' | 'similar' | 'older';

/** Nivel de grasa visceral. */
export type VisceralFatLevel = 'low' | 'normal' | 'high';

export const METABOLIC_AGE_LEVEL_LABELS: Record<MetabolicAgeLevel, string> = {
  younger: 'Metabolismo más joven',
  similar: 'En rango',
  older: 'Metabolismo más avanzado',
};

export const VISCERAL_FAT_LEVEL_LABELS: Record<VisceralFatLevel, string> = {
  low: 'Grasa visceral baja',
  normal: 'Grasa visceral normal',
  high: 'Grasa visceral alta',
};

/**
 * Gasto metabólico basal promedio de la población de referencia (kcal/día)
 * por edad y sexo. Se usa como referencia para determinar la edad metabólica:
 * si la TMB real es mayor que la promedio de su edad cronológica, el
 * metabolismo es más joven (y viceversa).
 */
const REFERENCE_BMR: Record<Sex, { age: number; bmr: number }[]> = {
  male: [
    { age: 18, bmr: 1620 },
    { age: 25, bmr: 1560 },
    { age: 30, bmr: 1530 },
    { age: 35, bmr: 1500 },
    { age: 40, bmr: 1480 },
    { age: 45, bmr: 1460 },
    { age: 50, bmr: 1440 },
    { age: 55, bmr: 1420 },
    { age: 60, bmr: 1400 },
    { age: 65, bmr: 1380 },
    { age: 70, bmr: 1360 },
    { age: 75, bmr: 1340 },
    { age: 80, bmr: 1320 },
  ],
  female: [
    { age: 18, bmr: 1230 },
    { age: 25, bmr: 1200 },
    { age: 30, bmr: 1180 },
    { age: 35, bmr: 1160 },
    { age: 40, bmr: 1150 },
    { age: 45, bmr: 1130 },
    { age: 50, bmr: 1120 },
    { age: 55, bmr: 1100 },
    { age: 60, bmr: 1090 },
    { age: 65, bmr: 1070 },
    { age: 70, bmr: 1050 },
    { age: 75, bmr: 1030 },
    { age: 80, bmr: 1010 },
  ],
};

/**
 * Perímetro de cintura promedio de la población de referencia (cm) por edad
 * y sexo. Sirve para saber si la cintura de la clienta está por encima o por
 * debajo de lo esperable para su edad y sexo.
 */
const REFERENCE_WAIST: Record<Sex, { age: number; waist: number }[]> = {
  male: [
    { age: 18, waist: 82 },
    { age: 25, waist: 86 },
    { age: 35, waist: 91 },
    { age: 45, waist: 94 },
    { age: 55, waist: 96 },
    { age: 65, waist: 97 },
    { age: 75, waist: 97 },
  ],
  female: [
    { age: 18, waist: 70 },
    { age: 25, waist: 72 },
    { age: 35, waist: 76 },
    { age: 45, waist: 79 },
    { age: 55, waist: 81 },
    { age: 65, waist: 83 },
    { age: 75, waist: 84 },
  ],
};

/** Interpolación lineal sobre una tabla {age, value} ordenada por edad. */
const interpolateByAge = (table: { age: number; value: number }[], age: number): number => {
  if (!table.length) return 0;
  if (age <= table[0].age) return table[0].value;
  const last = table[table.length - 1];
  if (age >= last.age) return last.value;
  for (let i = 0; i < table.length - 1; i++) {
    const a = table[i];
    const b = table[i + 1];
    if (age >= a.age && age <= b.age) {
      const t = (age - a.age) / (b.age - a.age);
      return a.value + (b.value - a.value) * t;
    }
  }
  return last.value;
};

/** TMB de referencia para el sexo y la edad indicados. */
export const referenceBmr = (sex: Sex, ageYears: number): number =>
  interpolateByAge(
    REFERENCE_BMR[sex].map((r) => ({ age: r.age, value: r.bmr })),
    ageYears
  );

/** Cintura de referencia para el sexo y la edad indicados. */
export const referenceWaist = (sex: Sex, ageYears: number): number =>
  interpolateByAge(
    REFERENCE_WAIST[sex].map((r) => ({ age: r.age, value: r.waist })),
    ageYears
  );

export interface MetabolicAgeInput {
  sex: Sex;
  ageYears: number;
  /** Tasa metabólica basal en kcal/día (salida de `computeEnergy`). */
  bmr?: number | null;
}

export interface MetabolicAgeResult {
  ok: boolean;
  metabolicAge: number | null;
  /** Diferencia entre la edad metabólica y la cronológica (negativo = más joven). */
  differenceYears: number | null;
  level: MetabolicAgeLevel | null;
  missing: string[];
}

/**
 * Edad metabólica: la edad cronológica cuya TMB promedio poblacional
 * coincide con la TMB real de la persona. Una TMB por encima del promedio
 * de su edad equivale a un metabolismo más joven.
 */
export const computeMetabolicAge = (input: MetabolicAgeInput): MetabolicAgeResult => {
  const { sex, ageYears, bmr } = input;
  const missing: string[] = [];
  if (!sex) missing.push('sex');
  if (!ageYears) missing.push('ageYears');
  if (!bmr) missing.push('bmr');
  if (missing.length) {
    return { ok: false, metabolicAge: null, differenceYears: null, level: null, missing };
  }

  const table = REFERENCE_BMR[sex];
  const value = Number(bmr);

  let metabolicAge: number;
  const first = table[0];
  const last = table[table.length - 1];
  if (value >= first.bmr) {
    // Metabolismo por encima del promedio de la población más joven.
    const slope = (first.bmr - table[1].bmr) / (table[1].age - first.age);
    metabolicAge = first.age + (value - first.bmr) / slope;
  } else if (value <= last.bmr) {
    const slope = (table[table.length - 2].bmr - last.bmr) / (last.age - table[table.length - 2].age);
    metabolicAge = last.age + (value - last.bmr) / slope;
  } else {
    metabolicAge = first.age;
    for (let i = 0; i < table.length - 1; i++) {
      const a = table[i];
      const b = table[i + 1];
      if (value <= a.bmr && value >= b.bmr) {
        const t = (a.bmr - value) / (a.bmr - b.bmr);
        metabolicAge = a.age + t * (b.age - a.age);
        break;
      }
    }
  }

  metabolicAge = Math.round(Math.min(90, Math.max(15, metabolicAge)));
  const differenceYears = metabolicAge - ageYears;
  const level: MetabolicAgeLevel =
    differenceYears <= -2 ? 'younger' : differenceYears >= 2 ? 'older' : 'similar';

  return {
    ok: true,
    metabolicAge,
    differenceYears,
    level,
    missing: [],
  };
};

export interface VisceralFatInput {
  sex: Sex;
  ageYears: number;
  /** Masa grasa total en kg (salida de `computeComposition`). */
  fatMassKg?: number | null;
  /** Perímetro de cintura en cm. */
  waistCm?: number | null;
  /** Captura manual opcional; si viene, se usa en lugar de la estimación. */
  manualKg?: number | null;
}

export interface VisceralFatResult {
  ok: boolean;
  /** Masa de grasa visceral estimada (o capturada) en kg. */
  visceralFatKg: number | null;
  /** Índice 1-20 al estilo de las básculas de consumo. */
  visceralFatIndex: number | null;
  level: VisceralFatLevel | null;
  /** `derived` = estimación; `manual` = captura directa. */
  source: 'derived' | 'manual' | null;
  missing: string[];
}

/**
 * Proporción base de la masa grasa total que se considera visceral
 * (abdomen profundo) en una persona con cintura de referencia.
 */
const VISCERAL_SHARE_BASE: Record<Sex, number> = { male: 0.15, female: 0.08 };

/** Masa de grasa visceral esperada (kg) para una persona de referencia. */
const VISCERAL_EXPECTED_KG: Record<Sex, number> = { male: 1.6, female: 0.8 };

/**
 * Grasa visceral estimada.
 *
 * Método: de la masa grasa total se estima la fracción depositada en el
 * compartimento visceral según el sexo (8% en mujeres, 15% en hombres) y se
 * ajusta según el exceso de cintura frente a la referencia poblacional de su
 * edad y sexo. El índice 1-20 se normaliza contra la masa visceral esperada.
 */
export const computeVisceralFat = (input: VisceralFatInput): VisceralFatResult => {
  const { sex, ageYears, fatMassKg, waistCm, manualKg } = input;
  const missing: string[] = [];
  if (!sex) missing.push('sex');
  if (!ageYears) missing.push('ageYears');
  if (!fatMassKg && !manualKg) missing.push('fatMassKg');
  if (!manualKg && !waistCm) missing.push('waistCm');
  if (missing.length) {
    return {
      ok: false,
      visceralFatKg: null,
      visceralFatIndex: null,
      level: null,
      source: null,
      missing,
    };
  }

  const expectedKg = VISCERAL_EXPECTED_KG[sex] + 0.01 * (ageYears - 30);
  let visceralFatKg: number;
  let source: 'derived' | 'manual';

  if (manualKg) {
    visceralFatKg = Number(manualKg);
    source = 'manual';
  } else {
    const waistRef = referenceWaist(sex, ageYears);
    const waistRatio = waistRef > 0 ? Number(waistCm) / waistRef : 1;
    const share = Math.min(
      0.45,
      Math.max(0.04, VISCERAL_SHARE_BASE[sex] + 0.25 * (waistRatio - 1))
    );
    visceralFatKg = Number(fatMassKg) * share;
    source = 'derived';
  }

  visceralFatKg = Math.max(0, round(visceralFatKg));
  const ratio = expectedKg > 0 ? visceralFatKg / expectedKg : 0;
  const visceralFatIndex = Math.min(20, Math.max(1, Math.round(ratio * 10)));
  const level: VisceralFatLevel = ratio < 0.5 ? 'low' : ratio > 1.2 ? 'high' : 'normal';

  return {
    ok: true,
    visceralFatKg,
    visceralFatIndex,
    level,
    source,
    missing: [],
  };
};

export interface MuscleMassInput {
  /** Masa magra en kg (salida de `computeComposition`). */
  leanMassKg?: number | null;
  /** Peso total en kg. */
  weightKg?: number | null;
}

export interface MuscleMassResult {
  ok: boolean;
  /** Masa muscular esquelética estimada en kg. */
  muscleMassKg: number | null;
  method: 'from_lean_mass' | null;
  missing: string[];
}

/**
 * Fracción de la masa magra que corresponde a masa muscular esquelética.
 * El resto es agua, órganos, tejido conectivo y hueso.
 */
const MUSCLE_FRACTION_OF_LEAN_MASS = 0.55;

/**
 * Masa muscular derivada de la masa magra.
 * El campo manual `nutrition_progress.muscle_kg` se conserva intacto: este
 * valor es una estimación y convive con la captura manual.
 */
export const computeMuscleMass = (input: MuscleMassInput): MuscleMassResult => {
  const { leanMassKg, weightKg } = input;
  if (!leanMassKg) {
    return { ok: false, muscleMassKg: null, method: null, missing: ['leanMassKg'] };
  }
  return {
    ok: true,
    muscleMassKg: round(Number(leanMassKg) * MUSCLE_FRACTION_OF_LEAN_MASS),
    method: 'from_lean_mass',
    missing: [],
  };
};

// ---- Autoverificación (se ejecuta al cargar el módulo) ----
const approx = (a: number, b: number, tol = 0.1) => Math.abs(a - b) <= tol;

// JP3 varón, 30 años, pliegues 10/20/30 mm -> S=60
(() => {
  const res = computeComposition({
    sex: 'male',
    ageYears: 30,
    formula: 'jp3',
    skinfolds: { chest: 10, abdominal: 20, thigh: 30 },
    weightKg: 80,
  });
  const expectedDensity =
    1.10938 - 0.0008267 * 60 + 0.0000016 * 3600 - 0.0002574 * 30;
  const expectedPct = 495 / expectedDensity - 450;
  console.assert(res.ok, 'JP3 debe calcular');
  console.assert(approx(res.bodyDensity as number, expectedDensity, 1e-4), 'JP3 densidad');
  console.assert(approx(res.percentFat as number, expectedPct, 0.01), 'JP3 %grasa');
})();

// Mifflin varón, 30 años, 80 kg, 180 cm -> 10*80 + 6.25*180 - 5*30 + 5
(() => {
  const res = computeEnergy({
    sex: 'male',
    ageYears: 30,
    weightKg: 80,
    heightCm: 180,
    activityLevel: 'moderate',
    goal: 'maintenance',
  });
  console.assert(approx(res.bmr as number, 1780), 'Mifflin TMB = 1780');
  console.assert(approx(res.tdee as number, 1780 * 1.55, 0.5), 'TDEE moderado');
})();

// Katch-McArdle con masa magra 64 kg -> 370 + 21.6*64
(() => {
  const res = computeEnergy({
    sex: 'male',
    ageYears: 30,
    weightKg: 80,
    heightCm: 180,
    leanMassKg: 64,
    activityLevel: 'sedentary',
    bmrFormula: 'katch',
  });
  console.assert(approx(res.bmr as number, 370 + 21.6 * 64), 'Katch-McArdle');
})();

// Edad metabólica: TMB por encima del promedio de la edad -> más joven
(() => {
  const ref30 = referenceBmr('male', 30);
  const younger = computeMetabolicAge({ sex: 'male', ageYears: 30, bmr: ref30 + 150 });
  const same = computeMetabolicAge({ sex: 'male', ageYears: 30, bmr: ref30 });
  const older = computeMetabolicAge({ sex: 'male', ageYears: 30, bmr: ref30 - 150 });
  console.assert(younger.ok && (younger.metabolicAge as number) < 30, 'Edad metabólica más joven');
  console.assert(same.ok && (same.metabolicAge as number) === 30, 'Edad metabólica = cronológica');
  console.assert(older.ok && (older.metabolicAge as number) > 30, 'Edad metabólica más avanzada');
  console.assert(younger.level === 'younger' && same.level === 'similar' && older.level === 'older',
    'Nivel de edad metabólica');
})();

// Edad metabólica sin TMB no se puede calcular
(() => {
  const res = computeMetabolicAge({ sex: 'female', ageYears: 28, bmr: null });
  console.assert(!res.ok && res.missing.includes('bmr'), 'Edad metabólica sin TMB');
})();

// Grasa visceral: cintura de referencia y cintura elevada
(() => {
  const waistRef = referenceWaist('female', 30);
  const atRef = computeVisceralFat({
    sex: 'female',
    ageYears: 30,
    fatMassKg: 12,
    waistCm: waistRef,
  });
  const high = computeVisceralFat({
    sex: 'female',
    ageYears: 30,
    fatMassKg: 12,
    waistCm: waistRef * 1.25,
  });
  console.assert(atRef.ok && (atRef.visceralFatKg as number) > 0, 'Grasa visceral estimada');
  console.assert(atRef.source === 'derived', 'Grasa visceral: fuente derivada');
  console.assert((high.visceralFatKg as number) > (atRef.visceralFatKg as number),
    'Grasa visceral sube con la cintura');
  console.assert((atRef.visceralFatIndex as number) >= 1 && (atRef.visceralFatIndex as number) <= 20,
    'Índice de grasa visceral entre 1 y 20');
})();

// Grasa visceral manual gana a la estimación
(() => {
  const res = computeVisceralFat({
    sex: 'female',
    ageYears: 30,
    fatMassKg: 12,
    waistCm: 80,
    manualKg: 2.5,
  });
  console.assert(res.source === 'manual' && res.visceralFatKg === 2.5, 'Grasa visceral manual');
})();

// Masa muscular derivada de la masa magra
(() => {
  const res = computeMuscleMass({ leanMassKg: 40, weightKg: 60 });
  console.assert(res.ok && approx(res.muscleMassKg as number, 22, 0.01), 'Masa muscular 55% de la magra');
  const empty = computeMuscleMass({ leanMassKg: null });
  console.assert(!empty.ok && empty.missing.includes('leanMassKg'), 'Masa muscular sin masa magra');
})();

console.log('nutrition-formulas: casos OK');